import { useState } from "react";
import { IOrbProps } from "./App";
import { useApi } from './components/useApi';
import { useUserContext } from './components/useUserContext';
import { useDataContext } from './components/useDataContext';
import { useIntegration } from './components/useIntegration';
import React from "react";
import { HashRouter, Switch, Route } from 'react-router-dom';
import { ErrorBoundary } from "./components/ErrorBoundary";
import { IntegrationContext } from "./components/IntegrationContext";
import { DataContext } from "./components/DataContext";
import { OrbUserContext } from "./components/OrbUserContext";
import { CrLoadingOverlay } from './components/cr/CrLoadingOverlay';
import { AppGlobals, AppIDs } from "./AppGlobals";
import LoadingWhite from "./LoadingWhite";
import { withErrorHandling } from './components/withErrorHandling';
import { NotAuthorised, NotFound } from "./Routes";
import { CorporateWorkflowsMenu } from "./components/CorporateWorkflowsMenu";
import App03WorkflowsAdmin from "./components/app03Workflows/App03WorkflowsAdmin";
import WorkflowTasks from "./components/app03Workflows/WorkflowTasks";
import { App03WorkflowsWelcome } from "./components/app03Workflows/App03WorkflowsWelcome";
import { Insights } from "./components/Insights";
import { App03Frameworks } from "./types/App03Workflow";
import { IEntityDescription1 } from "./types";
import { TopBanner } from "./TopBanner";
import { MenuToggle } from "./components/MenuToggle";
import { useHistory, useLocation } from "react-router-dom";
import { buildCorporateWorkflowsBreadcrumb } from "./breadcrumbs";
import AiChat from "./components/AI/AiChat";


const CorporateWorkflowsApp: React.FunctionComponent<IOrbProps> = (props: IOrbProps) => {

    const history = useHistory();
    const location = useLocation();

    const [selectedWorkItemsFilter, setSelectedWorkItemsFilter] = useState<string | null>(null);
    const [entityDescriptionForMenu, setEntityDescriptionForMenu] = useState<IEntityDescription1>();
    const [lastPortfolioIdForChilds, setLastPortfolioIdForChilds] = useState(null);
    const [lastProjectIdForChilds, setLastProjectIdForChilds] = useState(null);
    const [lastTemplateIdForChilds, setLastTemplateIdForChilds] = useState(null);
    const [backlogStagesCount, setBacklogStagesCount] = useState(0);


    const { dataServices, errorHandling } = props;
    const apiConnected = useApi(dataServices);
    const uc = useUserContext(apiConnected, dataServices, errorHandling);
    const ld = useDataContext(apiConnected, dataServices, errorHandling);

    const integration = useIntegration({
        disableDirectorateManagement: false,
        disableGroupManagement: false,
        disablePartnerOrganisationManagement: false,
        disableProjectManagement: false,
        disableUserManagement: false,
        dataSourceName: ''
    });

    const routeProps = { ...props, apiConnected: apiConnected, ...uc, ...ld };

    const commonProps = {
        lastPortfolioIdForChilds: lastPortfolioIdForChilds,
        setLastPortfolioIdForChilds: setLastPortfolioIdForChilds,
        lastProjectIdForChilds: lastProjectIdForChilds,
        setLastProjectIdForChilds: setLastProjectIdForChilds,
        lastTemplateIdForChilds: lastTemplateIdForChilds,
        setLastTemplateIdForChilds: setLastTemplateIdForChilds,
        entityDescriptionForMenu: entityDescriptionForMenu,
        onEntityDescriptionChange: setEntityDescriptionForMenu,
        selectedWorkItemsFilter: selectedWorkItemsFilter,
        onWorkItemsFilterChange: setSelectedWorkItemsFilter,
        menuData: props.menuData,
        frameworkID: App03Frameworks.Kanban,
        onBacklogStagesCountChange: setBacklogStagesCount,
        ...routeProps
    };

    const [isNavigationVisible, setNavigationVisible] = useState(false);
    const toggleNavigation = () => setNavigationVisible(!isNavigationVisible);

    if (uc.userContext.UserId === null) return (<LoadingWhite />);

    const appName = props.menuData.filter(m => m.ID === AppIDs.CORWF)[0].AppName;

    const breadcrumbItems = buildCorporateWorkflowsBreadcrumb({
        customerTitle: AppGlobals.CustomerTitle,
        appName,
        location,
        history,
        lastPortfolioIdForChilds
    });

    return (
        <React.Fragment>
            <ErrorBoundary>
                <IntegrationContext.Provider value={integration}>
                    <DataContext.Provider value={ld}>
                        <OrbUserContext.Provider value={uc}>

                            <div className="main">
                                <div className="container">

                                    {/* LEFT NAVIGATION (drawer) */}
                                    <div className={`navigation ${isNavigationVisible ? 'show' : ''}`}>
                                        {!uc.userContext.UserId
                                            ? <CrLoadingOverlay isLoading={true} />
                                            : <CorporateWorkflowsMenu backlogStagesCount={backlogStagesCount} {...commonProps} onLinkClicked={toggleNavigation} />
                                        }
                                    </div>

                                    {/* BACKDROP for mobile drawer */}
                                    <div
                                        className={`nav-backdrop ${isNavigationVisible ? 'show' : ''}`}
                                        onClick={toggleNavigation}
                                        aria-hidden={!isNavigationVisible}
                                    />

                                    {/* RIGHT CONTENT AREA */}
                                    <div className="right-area">
                                        <div className="top-banner">
                                            <MenuToggle onToggleNavigation={toggleNavigation} />

                                            <TopBanner
                                                customerTitle={AppGlobals.CustomerTitle}
                                                userName={AppGlobals.UserTitle}
                                                breadcrumbItems={breadcrumbItems}
                                                onLogout={props.onLogout}
                                            />
                                        </div>

                                        <div className="content">
                                            <HashRouter>
                                                <Switch>
                                                    <Route exact path="/a1/welcome" {...routeProps}>
                                                        <App03WorkflowsWelcome {...commonProps} />
                                                    </Route>

                                                    <Route exact path="/a1/insights" {...routeProps}>
                                                        <Insights dashboardRef='CORWF-Dashboard' />
                                                    </Route>

                                                    <Route exact path="/a1/ai-chats" {...routeProps}>
                                                        <App03WorkflowsAdmin {...commonProps} entity="AIChats" />
                                                    </Route>

                                                    <Route exact path="/a1/ask-cloe/:chatId" {...routeProps}>
                                                        <AiChat {...commonProps} />
                                                    </Route>

                                                    <Route path="/a1/cwf-portfolios" {...routeProps}>
                                                        <App03WorkflowsAdmin {...commonProps} entity="App03Portfolios" />
                                                    </Route>

                                                    <Route path="/a1/workflows" {...routeProps}>
                                                        <App03WorkflowsAdmin {...commonProps} entity="Workflows" />
                                                    </Route>

                                                    <Route path="/a1/epics/:workflowIdForEpics" {...routeProps}>
                                                        <App03WorkflowsAdmin {...commonProps} entity="Epics" />
                                                    </Route>

                                                    <Route path="/a1/workflows-for-portfolio/:portfolioIdForWorkflows" {...routeProps}>
                                                        <App03WorkflowsAdmin {...commonProps} entity="Workflows" />
                                                    </Route>

                                                    <Route path="/a1/workflow-users/:workflowIdForWorkflowUsers" {...routeProps}>
                                                        <App03WorkflowsAdmin {...commonProps} entity="WorkflowUsers" />
                                                    </Route>

                                                    <Route path="/a1/workflow-tasks/:workflowIdForTasks" {...routeProps}>
                                                        <WorkflowTasks {...commonProps} />
                                                    </Route>

                                                    <Route path="/a1/settings/templates" {...routeProps}>
                                                        <App03WorkflowsAdmin {...commonProps} entity="App03Templates" />
                                                    </Route>

                                                    <Route path="/a1/settings/work-item-types/:templateIDForWorkItemTypes" {...routeProps}>
                                                        <App03WorkflowsAdmin {...commonProps} entity="WorkItemTypes" />
                                                    </Route>

                                                    <Route path="/not-authorised">
                                                        <NotAuthorised />
                                                    </Route>

                                                    <Route path="*">
                                                        <NotFound />
                                                    </Route>
                                                </Switch>
                                            </HashRouter>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </OrbUserContext.Provider>
                    </DataContext.Provider>
                </IntegrationContext.Provider>
            </ErrorBoundary>
        </React.Fragment>
    );
};

export const CorporateWorkflowsAppWithErrorHandling =
    withErrorHandling(CorporateWorkflowsApp);
